import React from 'react';
import { AlertTriangle, Phone, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmergencySafetyBannerProps {
  safetyDisclaimer?: string;
}

const EmergencySafetyBanner: React.FC<EmergencySafetyBannerProps> = ({ safetyDisclaimer }) => {
  const handleCall911 = () => {
    window.location.href = 'tel:911';
  };

  return (
    <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-red-900 dark:text-red-100">
            Medical Emergency
          </h3>
          <p className="text-sm text-red-800 dark:text-red-200 mt-1">
            If you're experiencing a medical emergency, call 911 immediately.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <button
          onClick={handleCall911}
          className={cn(
            'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white',
            'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800',
            'transition-colors shadow-sm'
          )}
        >
          <Phone className="w-4 h-4" />
          Call 911
        </button>

        <a
          href="https://www.google.com/maps/search/emergency+room+near+me"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium',
            'bg-white dark:bg-gray-900 text-red-700 dark:text-red-300',
            'border border-red-300 dark:border-red-700',
            'hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors'
          )}
        >
          <MapPin className="w-4 h-4" />
          Find Nearest ER
        </a>
      </div>

      {/* Safety Disclaimer */}
      {safetyDisclaimer && (
        <div className="pt-3 border-t border-red-200 dark:border-red-800">
          <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
            <span className="font-semibold">Important:</span> {safetyDisclaimer}
          </p>
        </div>
      )}

      {/* Default Disclaimer */}
      {!safetyDisclaimer && (
        <div className="pt-3 border-t border-red-200 dark:border-red-800">
          <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
            <span className="font-semibold">Disclaimer:</span> This AI assistant is not a
            substitute for professional medical care. For emergencies, always call 911 or visit
            your nearest emergency room.
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(EmergencySafetyBanner);
